import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default function TermsOfService() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container maxWidth="md" sx={{ mt: isMobile ? '80px' : '128px', mb: 6, px: isMobile ? 2 : 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Terms of Service
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Last updated: May 12, 2026
      </Typography>

      <Section title="1. Acceptance of Terms">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          By accessing or using The Big Board ("Service"), you agree to be bound by these Terms of Service.
          If you do not agree, do not use the Service. The Big Board is a college football pick prediction
          game for entertainment purposes only.
        </Typography>
      </Section>

      <Section title="2. Eligibility">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          You must be at least 18 years old to use the Service. By creating an account, you represent that
          you are at least 18 years of age.
        </Typography>
      </Section>

      <Section title="3. Account Responsibilities">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          You are responsible for maintaining the confidentiality of your account credentials. You agree to
          provide accurate information during registration and to keep your account information up to date.
        </Typography>
      </Section>

      <Section title="4. SMS Messaging Terms">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          <strong>Program Name:</strong> BigBoard SMS Notification Program.
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 1 }}>
          By providing your phone number, completing OTP verification, and explicitly enabling notification
          toggles in your Profile, you consent to receive transactional SMS messages from The Big Board.
          The program sends four message types: (1) phone verification one-time codes, (2) "new games
          published" alerts when a new week opens for picks, (3) kickoff reminders sent 2 hours before the
          first game of a week if you have not submitted all picks, and (4) leaderboard updates after
          scores finalize.
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 3, '& li': { mb: 0.5, lineHeight: 1.8 } }}>
          <li>Message frequency varies based on your notification preferences and game schedules.</li>
          <li>Message and data rates may apply depending on your mobile carrier plan.</li>
          <li>Reply <strong>STOP</strong> to any message to opt out of SMS notifications at any time.</li>
          <li>Reply <strong>HELP</strong> to any message for support information, or contact <a href="mailto:support@bigboard.football" style={{ color: '#555' }}>support@bigboard.football</a>.</li>
          <li>SMS notifications are optional and not required to use The Big Board.</li>
          <li>Carriers are not liable for delayed or undelivered messages.</li>
        </Box>
      </Section>

      <Section title="5. User Conduct">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          You agree not to use the Service for any unlawful purpose, to interfere with other users'
          enjoyment of the Service, or to attempt to gain unauthorized access to any part of the Service.
        </Typography>
      </Section>

      <Section title="6. Intellectual Property">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          All content, branding, and materials on The Big Board are the property of The Big Board or its
          licensors. Team names, logos, and related marks are the property of their respective owners and
          are used for informational purposes only.
        </Typography>
      </Section>

      <Section title="7. Disclaimer">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          The Service is provided "as is" without warranties of any kind. The Big Board is an entertainment
          service and does not involve real-money wagering. We do not guarantee the accuracy of game data,
          scores, or odds information.
        </Typography>
      </Section>

      <Section title="8. Limitation of Liability">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          To the fullest extent permitted by law, The Big Board shall not be liable for any indirect,
          incidental, special, or consequential damages arising from your use of the Service.
        </Typography>
      </Section>

      <Section title="9. Changes to Terms">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We may update these Terms at any time. Continued use of the Service after changes constitutes
          acceptance of the revised Terms.
        </Typography>
      </Section>

      <Section title="10. Contact">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          For questions about these Terms, contact us at{' '}
          <a href="mailto:support@bigboard.football" style={{ color: '#555' }}>support@bigboard.football</a>.
        </Typography>
      </Section>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        See also our <RouterLink to="/privacy" style={{ color: '#666' }}>Privacy Policy</RouterLink>.
      </Typography>
    </Container>
  );
}
